import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { RefreshCw, ExternalLink, Trash2, Search, RotateCcw, Info, CheckCircle2, EyeOff, ClipboardCheck, History, FileText } from "lucide-react";
import { isDemoWorkspace, api, getToken, getUser, reviewState, type Conversion, type ConversionSortField, type StageLogEntry } from "../api";
import { Pagination } from "../components/Pagination";
import { SortHeader, type SortOrder } from "../components/SortHeader";
import { Modal, useConfirm } from "../components/Modal";
import { Alert, MoreMenu, PageHeader } from "../components/Page";

type StatusFilter = "all" | Conversion["status"] | "review" | "live";

// Track E — operator-selectable models. Labels match the visible UI; values
// are OpenRouter model ids passed verbatim. Keep in sync with the pricing
// table in packages/pipeline/src/sdoc_pipeline/pipeline.py::estimate_cost.
const MODEL_OPTIONS: { label: string; value: string }[] = [
  { label: "Sonnet 4.6 (default, balanced)", value: "anthropic/claude-sonnet-4-6-20251001" },
  { label: "Haiku 4.5 (cheapest, fastest)", value: "anthropic/claude-haiku-4-5-20251001" },
];

export function Workbench() {
  const [rows, setRows] = useState<Conversion[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [search, setSearch] = useState("");
  const [detailsRow, setDetailsRow] = useState<Conversion | null>(null);
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(50);
  const [sort, setSort] = useState<ConversionSortField>("created_at");
  const [order, setOrder] = useState<SortOrder>("desc");

  const user = getUser();
  const isSuper = user?.role === "superadmin";

  const refresh = useCallback(async () => {
    try {
      const fresh = await api.listConversions({
        status_filter: statusFilter,
        sort, order, limit: perPage, offset: page * perPage,
        q: search.trim() || undefined,
      });
      setRows(fresh.items);
      setTotal(fresh.total);
      // Keep the open modal in sync with the polled data so a re-convert
      // started from the modal reflects its new status without re-opening.
      setDetailsRow((cur) => (cur ? fresh.items.find((c) => c.id === cur.id) ?? cur : cur));
      setError(null);
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    finally { setLoading(false); }
  }, [statusFilter, sort, order, page, perPage, search]);
  useEffect(() => { refresh(); }, [refresh]);
  // Reset to page 0 when the result set the user is viewing changes.
  useEffect(() => { setPage(0); }, [statusFilter, sort, order, perPage, search]);

  function onSort(field: ConversionSortField, nextOrder: SortOrder) {
    setSort(field); setOrder(nextOrder);
  }
  useEffect(() => {
    if (!rows.some((c) => c.status === "queued" || c.status === "running")) return;
    const t = window.setInterval(refresh, 3000);
    return () => window.clearInterval(t);
  }, [rows, refresh]);

  function openInReader(id: string) {
    const base = (import.meta.env.VITE_READER_BASE as string | undefined) || "http://127.0.0.1:5173";
    window.open(`${base}/?conversion=${id}&token=${getToken() || ""}`, "_blank", "noopener,noreferrer");
  }

  const [confirm, confirmNode] = useConfirm();

  async function del(id: string, filename: string | null) {
    const ok = await confirm({
      title: "Delete this conversion?",
      message: <>Removes <strong>{filename || "this conversion"}</strong>. Readers who had the link open will no longer see the accessible version.</>,
      confirmLabel: "Delete",
      tone: "danger",
    });
    if (!ok) return;
    try { await api.deleteConversion(id); await refresh(); }
    catch (err) { setError(err instanceof Error ? err.message : String(err)); }
  }

  const canReview = !!user && ["owner", "admin", "editor", "superadmin"].includes(String(user.role));

  // Version history of one document, shown under its row.
  const [openVersions, setOpenVersions] = useState<string | null>(null);
  const [versionRows, setVersionRows] = useState<Record<string, Conversion[]>>({});
  async function toggleVersions(documentId: string) {
    if (openVersions === documentId) { setOpenVersions(null); return; }
    setOpenVersions(documentId);
    try {
      const res = await api.listConversions({ document_id: documentId, sort: "created_at", order: "desc", limit: 50 });
      setVersionRows((m) => ({ ...m, [documentId]: res.items }));
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
  }

  async function approve(c: Conversion) {
    const ok = await confirm({
      title: "Publish this document?",
      message: "Approve only after checking the flagged pages against the original PDF. Visitors will see this version on the site.",
      confirmLabel: "Approve and publish",
    });
    if (!ok) return;
    try {
      const updated = await api.approveConversion(c.id);
      setRows((rs) => rs.map((r) => (r.id === updated.id ? updated : r)));
      setDetailsRow((d) => (d && d.id === updated.id ? updated : d));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  async function unpublish(c: Conversion) {
    const ok = await confirm({
      title: "Take this document off the site?",
      message: "Visitors will get the original PDF link again until the document is approved.",
      confirmLabel: "Unpublish",
      tone: "danger",
    });
    if (!ok) return;
    try {
      const updated = await api.unpublishConversion(c.id);
      setRows((rs) => rs.map((r) => (r.id === updated.id ? updated : r)));
      setDetailsRow((d) => (d && d.id === updated.id ? updated : d));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  async function reconvert(id: string, modelOverride: string | null) {
    try {
      await api.reconvertConversion(id, modelOverride);
      await refresh();
    } catch (err) { setError(err instanceof Error ? err.message : String(err)); }
  }

  // Search is server-side (matches conversion/document id, filename,
  // source_url) so we don't filter again on the client.
  const closeDetails = useCallback(() => setDetailsRow(null), []);
  const sortValue = `${sort}:${order}`;
  const sortOptions = SORT_OPTIONS.filter((o) => isSuper || !o.superOnly);
  const demo = isDemoWorkspace();

  // Everything a row's buttons need, shared by the wide table and the narrow list.
  const rowProps = (c: Conversion) => ({
    c, canReview, demo,
    versionsOpen: openVersions === c.document_id,
    onDetails: () => setDetailsRow(c),
    onOpen: () => openInReader(c.id),
    onVersions: () => toggleVersions(c.document_id),
    onApprove: () => approve(c),
    onDelete: () => del(c.id, c.filename ?? null),
  });
  const versions = (c: Conversion) => (
    <VersionsList c={c} rows={versionRows[c.document_id]} isSuper={isSuper} onOpen={openInReader} />
  );

  return (
    <div>
      <PageHeader
        title="Workbench"
        description="Every conversion in your workspace. Pick PDFs on Discovered or the Conversion plan to convert more."
        actions={<button onClick={refresh} className="btn-secondary"><RefreshCw className="h-4 w-4" aria-hidden="true" /> Refresh</button>}
      />

      <div className="filter-bar" role="search">
        <div className="filter-grow">
          <label htmlFor="wb-search" className="field-label">Search</label>
          <div className="relative">
            <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" aria-hidden="true" />
            <input
              id="wb-search"
              className="input pl-9"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="File name, source URL or id"
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3 sm:flex sm:items-end">
          <div>
            <label htmlFor="wb-status" className="field-label">Status</label>
            <select id="wb-status" className="input" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}>
              <option value="all">All</option>
              <option value="review">Review needed</option>
              <option value="live">Live on site</option>
              <option value="queued">Queued</option>
              <option value="running">Running</option>
              <option value="succeeded">Succeeded</option>
              {isSuper && <option value="degraded">Degraded</option>}
              <option value="failed">Failed</option>
            </select>
          </div>
          <div>
            <label htmlFor="wb-sort" className="field-label">Sort by</label>
            <select
              id="wb-sort" className="input" value={sortValue}
              onChange={(e) => {
                const [f, o] = e.target.value.split(":");
                onSort(f as ConversionSortField, o as SortOrder);
              }}
            >
              {sortOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              {!sortOptions.some((o) => o.value === sortValue) && <option value={sortValue}>Custom</option>}
            </select>
          </div>
        </div>
      </div>

      {error && <Alert onDismiss={() => setError(null)}>{error}</Alert>}

      <div className="card overflow-hidden">
        {loading ? (
          <div className="card-body text-sm text-slate-500">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="card-body text-sm text-slate-500">No conversions match these filters.</div>
        ) : (
          <>
            {/* Wide screens: a table. */}
            <table className="hidden xl:table w-full text-sm">
              <caption className="sr-only">Conversions</caption>
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th scope="col" className="th">Document</th>
                  <th scope="col" className="th w-[190px]">
                    <SortHeader<ConversionSortField> label="Status" field="status" current={sort} order={order} onChange={onSort} />
                  </th>
                  <th scope="col" className="th w-[90px]">Pages</th>
                  {isSuper && (
                    <th scope="col" className="th w-[90px]">
                      <SortHeader<ConversionSortField> label="Cost" field="estimated_cost_usd" current={sort} order={order} onChange={onSort} />
                    </th>
                  )}
                  <th scope="col" className="th w-[140px]">
                    <SortHeader<ConversionSortField> label="Updated" field="created_at" current={sort} order={order} onChange={onSort} />
                  </th>
                  <th scope="col" className="th text-right w-[180px]"><span className="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {rows.map((c) => (
                  <Fragment key={c.id}>
                    <tr className="hover:bg-slate-50/60">
                      <td className="td max-w-0"><DocCell c={c} /></td>
                      <td className="td"><StatusCell c={c} isSuper={isSuper} /></td>
                      <td className="td text-slate-700 tabular-nums"><PagesCell c={c} /></td>
                      {isSuper && <td className="td text-slate-700 tabular-nums">{formatCost(c.estimated_cost_usd)}</td>}
                      <td className="td"><WhenCell c={c} /></td>
                      <td className="td"><RowActions {...rowProps(c)} /></td>
                    </tr>
                    {openVersions === c.document_id && (
                      <tr className="bg-slate-50">
                        <td colSpan={isSuper ? 6 : 5} className="td">{versions(c)}</td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>

            {/* Narrow screens: one card per conversion. */}
            <ul className="xl:hidden divide-y divide-slate-200" aria-label="Conversions">
              {rows.map((c) => (
                <li key={c.id} className="px-4 py-4 sm:px-5">
                  <div className="flex flex-col gap-3 md:flex-row md:items-start md:gap-6">
                    <div className="min-w-0 flex-1">
                      <DocCell c={c} />
                      <div className="mt-2"><StatusCell c={c} isSuper={isSuper} /></div>
                    </div>
                    <dl className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-slate-500 md:w-44 md:flex-col md:shrink-0">
                      <div><dt className="inline">Pages </dt><dd className="inline text-slate-800 tabular-nums"><PagesCell c={c} inline /></dd></div>
                      {isSuper && <div><dt className="inline">Cost </dt><dd className="inline text-slate-800 tabular-nums">{formatCost(c.estimated_cost_usd)}</dd></div>}
                      <div><dt className="sr-only">Updated</dt><dd className="inline"><WhenCell c={c} withLabel /></dd></div>
                    </dl>
                    <div className="md:shrink-0"><RowActions {...rowProps(c)} /></div>
                  </div>
                  {openVersions === c.document_id && <div className="mt-3 rounded-md bg-slate-50 p-3">{versions(c)}</div>}
                </li>
              ))}
            </ul>
          </>
        )}
        {!loading && total > 0 && (
          <Pagination
            page={page}
            perPage={perPage}
            total={total}
            onPage={setPage}
            onPerPage={setPerPage}
          />
        )}
      </div>

      {confirmNode}

      {detailsRow && (
        <DetailsModal
          row={detailsRow}
          showCost={isSuper}
          canReview={canReview}
          onClose={closeDetails}
          onReconvert={(modelOverride) => reconvert(detailsRow.id, modelOverride)}
          onApprove={() => approve(detailsRow)}
          onUnpublish={() => unpublish(detailsRow)}
        />
      )}
    </div>
  );
}

const SORT_OPTIONS: { value: string; label: string; superOnly?: boolean }[] = [
  { value: "created_at:desc", label: "Newest first" },
  { value: "created_at:asc", label: "Oldest first" },
  { value: "completed_at:desc", label: "Recently finished" },
  { value: "status:asc", label: "Status" },
  { value: "forms_detected:desc", label: "Most forms" },
  { value: "estimated_cost_usd:desc", label: "Highest cost", superOnly: true },
];

function DocCell({ c }: { c: Conversion }) {
  const name = c.filename ? c.filename.replace(/\.pdf$/i, "") : null;
  return (
    <div className="min-w-0">
      <div className="font-medium text-ink break-words line-clamp-2 xl:block xl:truncate" title={c.filename ?? undefined}>
        {name ?? <span className="text-slate-400 italic">(unknown file name)</span>}
      </div>
      <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-slate-500">
        <span className="font-mono">{c.id.slice(0, 8)}</span>
        {(c.version_count ?? 1) > 1 && (
          <span className="chip bg-indigo-50 text-indigo-700" title="Each conversion of this PDF is a version; this row shows the newest">
            v{c.version} of {c.version_count}
          </span>
        )}
        {c.site_domain && <span className="chip bg-slate-100 text-slate-600 max-w-full truncate">{c.site_domain}</span>}
        {c.forms_detected > 0 && (
          <span className="chip bg-slate-100 text-slate-600">{c.forms_detected} form{c.forms_detected === 1 ? "" : "s"}</span>
        )}
      </div>
    </div>
  );
}

function StatusCell({ c, isSuper }: { c: Conversion; isSuper: boolean }) {
  const reasons = reviewState(c) === "review" ? (c.review_reasons ?? []) : [];
  return (
    <div className="min-w-0">
      {renderStatusPill(c, isSuper)}
      {c.live_version != null && c.live_conversion_id !== c.id && (
        <div className="mt-1 text-xs text-emerald-700">Visitors see version {c.live_version} until this one is approved</div>
      )}
      {reasons.length > 0 && (
        <ul className="mt-1 text-xs text-amber-800 list-disc pl-4">
          {reasons.slice(0, 2).map((r) => <li key={r}>{r}</li>)}
          {reasons.length > 2 && <li className="list-none -ml-4 text-amber-700">+{reasons.length - 2} more in Details</li>}
        </ul>
      )}
    </div>
  );
}

function PagesCell({ c, inline = false }: { c: Conversion; inline?: boolean }) {
  return (
    <>
      {c.page_count ?? "—"}
      {c.page_range && (
        <span
          className={inline ? "text-slate-500" : "block text-xs text-slate-500"}
          title={`Only pages ${c.page_range} of the original were converted`}
        >
          {inline ? " " : ""}pp. {c.page_range} of {c.source_page_count}
        </span>
      )}
    </>
  );
}

/** The moment that matters for the row's state: finished, started or queued. */
function WhenCell({ c, withLabel = false }: { c: Conversion; withLabel?: boolean }) {
  const [label, iso] = c.completed_at ? ["Finished", c.completed_at]
    : c.started_at ? ["Started", c.started_at]
      : ["Queued", c.created_at];
  return (
    <time dateTime={iso} title={`${label} ${new Date(iso).toLocaleString()}`}>
      {withLabel && <span className="text-slate-500">{label} </span>}
      <span className="text-slate-700">{formatWhen(iso)}</span>
      {!withLabel && <span className="block text-xs text-slate-400">{label.toLowerCase()}</span>}
    </time>
  );
}

function formatWhen(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const time = d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  if (d.toDateString() === now.toDateString()) return `Today ${time}`;
  const opts: Intl.DateTimeFormatOptions = d.getFullYear() === now.getFullYear()
    ? { month: "short", day: "numeric" }
    : { month: "short", day: "numeric", year: "numeric" };
  return `${d.toLocaleDateString([], opts)}, ${time}`;
}

/** One main button (Review when there is a result to look at, else
 *  Details); everything else lives in the More menu. */
function RowActions({
  c, canReview, demo, versionsOpen, onDetails, onOpen, onVersions, onApprove, onDelete,
}: {
  c: Conversion; canReview: boolean; demo: boolean; versionsOpen: boolean;
  onDetails: () => void; onOpen: () => void; onVersions: () => void; onApprove: () => void; onDelete: () => void;
}) {
  const done = c.status === "succeeded" || c.status === "degraded";
  const reviewable = done || !!c.derived_from;
  const needsReview = reviewState(c) === "review";
  const canApprove = needsReview && canReview && c.is_latest !== false && !c.open_blocking;
  return (
    <div className="flex items-center gap-1 xl:justify-end">
      {reviewable ? (
        <Link
          to={`/workbench/${c.id}/review`}
          className={needsReview ? "btn-primary" : "btn-secondary"}
          aria-label={`Review ${c.filename ?? c.id.slice(0, 8)}${c.open_blocking ? `, ${c.open_blocking} differences to decide` : ""}`}
        >
          <ClipboardCheck className="h-4 w-4" aria-hidden="true" /> Review
          {!!c.open_blocking && <span className="rounded-full bg-rose-600 text-white text-[11px] leading-4 px-1.5">{c.open_blocking}</span>}
        </Link>
      ) : (
        <button type="button" onClick={onDetails} className="btn-secondary">
          <Info className="h-4 w-4" aria-hidden="true" /> Details
        </button>
      )}
      <MoreMenu
        label={`More actions for ${c.filename ?? c.id.slice(0, 8)}`}
        items={[
          { label: "Open in reader", icon: <ExternalLink className="h-4 w-4" aria-hidden="true" />, onClick: onOpen, hidden: !done },
          { label: "Approve and publish", icon: <CheckCircle2 className="h-4 w-4" aria-hidden="true" />, onClick: onApprove, hidden: !canApprove },
          { label: "Details and stage log", icon: <Info className="h-4 w-4" aria-hidden="true" />, onClick: onDetails, hidden: !reviewable },
          { label: versionsOpen ? "Hide earlier versions" : "Earlier versions", icon: <History className="h-4 w-4" aria-hidden="true" />, onClick: onVersions, hidden: (c.version_count ?? 1) <= 1 },
          { label: "Original PDF", icon: <FileText className="h-4 w-4" aria-hidden="true" />, href: c.source_url ?? undefined, hidden: !c.source_url },
          { label: "Delete", icon: <Trash2 className="h-4 w-4" aria-hidden="true" />, onClick: onDelete, tone: "danger", hidden: demo },
        ]}
      />
    </div>
  );
}

function VersionsList({ c, rows, isSuper, onOpen }: {
  c: Conversion; rows: Conversion[] | undefined; isSuper: boolean; onOpen: (id: string) => void;
}) {
  return (
    <>
      <div className="text-xs font-semibold text-slate-600 mb-2">All versions of {c.filename}</div>
      <ul className="space-y-2 sm:space-y-1.5">
        {(rows ?? []).map((v) => (
          <li key={v.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
            <span className="font-medium sm:w-20">Version {v.version}</span>
            <span className="text-slate-500 sm:w-44">{formatWhen(v.created_at)}</span>
            {renderStatusPill(v, isSuper)}
            {v.id === c.live_conversion_id && <span className="text-xs text-emerald-700">live on the site</span>}
            {v.is_latest === false && <span className="text-xs text-slate-500">older, read only</span>}
            {(v.status === "succeeded" || v.status === "degraded") && (
              <button onClick={() => onOpen(v.id)} className="btn-ghost !py-1"><ExternalLink className="h-4 w-4" aria-hidden="true" /> Open</button>
            )}
          </li>
        ))}
        {!rows && <li className="text-slate-500 text-sm">Loading…</li>}
      </ul>
    </>
  );
}

/** Finished conversions show what they mean for the site: "live" or
 *  "review needed" (held back until someone approves). Superadmins also see
 *  the technical "degraded" detail. */
function renderStatusPill(c: Conversion, isSuper: boolean) {
  const rs = reviewState(c);
  if (rs === "review") return <span className="pill-review">review needed</span>;
  if (rs === "live" && !(isSuper && c.status === "degraded")) return <span className="pill-succeeded">live</span>;
  const status = c.status;
  const shown: Conversion["status"] = (!isSuper && status === "degraded") ? "succeeded" : status;
  const label: string = shown === "succeeded" ? "ready"
    : shown === "degraded" ? "degraded"
      : shown === "failed" ? "failed"
        : shown === "queued" ? "queued"
          : shown === "running" ? "running"
            : shown;
  return <span className={`pill-${shown}`}>{label}</span>;
}

function formatCost(usd: number | undefined): string {
  if (usd === undefined || usd === null || usd === 0) return "—";
  if (usd < 0.01) return `<$0.01`;
  return `$${usd.toFixed(2)}`;
}

function formatNum(n: number | undefined): string {
  if (n === undefined || n === null) return "0";
  return n.toLocaleString();
}

interface DetailsModalProps {
  row: Conversion;
  showCost: boolean;
  onClose: () => void;
  onReconvert: (modelOverride: string | null) => Promise<void>;
  canReview: boolean;
  onApprove: () => void;
  onUnpublish: () => void;
}

function DetailsModal({ row, showCost, canReview, onClose, onReconvert, onApprove, onUnpublish }: DetailsModalProps) {
  const [selectedModel, setSelectedModel] = useState<string>(MODEL_OPTIONS[0].value);
  const [busy, setBusy] = useState(false);

  const stages: StageLogEntry[] = row.stage_log ?? [];
  const maxDuration = useMemo(
    () => Math.max(1, ...stages.map((s) => s.duration_ms || 0)),
    [stages]
  );

  const elapsedMs = row.started_at && row.completed_at
    ? new Date(row.completed_at).getTime() - new Date(row.started_at).getTime()
    : null;

  async function handleReconvert(modelOverride: string | null) {
    if (busy) return;
    setBusy(true);
    try {
      await onReconvert(modelOverride);
    } finally {
      setBusy(false);
    }
  }

  const reconvertDisabled = row.status === "queued" || row.status === "running" || busy;

  const footer = isDemoWorkspace() ? undefined : (
    <div className="flex w-full flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-end">
      <button
        onClick={() => handleReconvert(null)}
        disabled={reconvertDisabled}
        className="btn-secondary"
        title="Re-run conversion using the pipeline's default model"
      >
        <RotateCcw className="h-4 w-4" aria-hidden="true" /> Re-convert with default
      </button>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <label htmlFor="reconvert-model" className="sr-only">Model for re-convert</label>
        <select
          id="reconvert-model"
          className="input sm:!w-auto"
          value={selectedModel}
          onChange={(e) => setSelectedModel(e.target.value)}
          disabled={reconvertDisabled}
        >
          {MODEL_OPTIONS.map((m) => (
            <option key={m.value} value={m.value}>{m.label}</option>
          ))}
        </select>
        <button
          onClick={() => handleReconvert(selectedModel)}
          disabled={reconvertDisabled}
          className="btn-primary"
        >
          <RotateCcw className="h-4 w-4" aria-hidden="true" /> Re-convert with this model
        </button>
      </div>
    </div>
  );

  return (
    <Modal
      open
      onClose={onClose}
      size="xl"
      title={row.filename ?? <>Conversion <span className="font-mono text-sm text-slate-500">{row.id.slice(0, 8)}</span></>}
      subtitle={<>
        {renderStatusPill(row, showCost)}
        <span>id <span className="font-mono">{row.id.slice(0, 8)}</span></span>
        {elapsedMs !== null && <span>{(elapsedMs / 1000).toFixed(1)}s total</span>}
        {row.model_override && <span className="text-slate-700 break-all">override: <code>{row.model_override}</code></span>}
      </>}
      footer={footer}
    >
      <div className="space-y-6">
        {reviewState(row) === "review" && (
          <div className="rounded-md border border-amber-300 bg-amber-50 p-4" role="note">
            <div className="font-semibold text-amber-900">Held for review, not shown on the site</div>
            {row.review_reasons && row.review_reasons.length > 0 && (
              <ul className="mt-2 list-disc pl-5 text-sm text-amber-900">
                {row.review_reasons.map((r) => <li key={r}>{r}</li>)}
              </ul>
            )}
            <p className="mt-2 text-sm text-amber-900">
              Open it, compare the flagged pages with the original PDF, then approve, or re-convert below.
            </p>
            {canReview && (
              <button type="button" className="btn-primary mt-3" onClick={onApprove}>
                <CheckCircle2 className="h-4 w-4" aria-hidden="true" /> Approve and publish
              </button>
            )}
          </div>
        )}
        {reviewState(row) === "live" && canReview && (
          <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm">
            <span className="text-emerald-900">
              Live on the site{row.approved_at ? `, approved ${new Date(row.approved_at).toLocaleString()}` : ""}.
            </span>
            <button type="button" className="btn-ghost text-rose-700" onClick={onUnpublish}>
              <EyeOff className="h-4 w-4" aria-hidden="true" /> Unpublish
            </button>
          </div>
        )}
        {/* Document identity — same PDF might be re-converted multiple times, so
            knowing which file this row points at is the first thing to show. */}
        <section>
          <h3 className="text-xs uppercase tracking-wider text-slate-500 mb-2">Document</h3>
          <dl className="grid grid-cols-1 sm:grid-cols-[max-content_1fr] gap-x-4 gap-y-1 text-sm">
            <dt className="text-slate-500">File name</dt>
            <dd className="text-slate-900 break-all mb-1 sm:mb-0">{row.filename ?? <span className="text-slate-400 italic">unknown</span>}</dd>
            {row.source_url && (
              <>
                <dt className="text-slate-500">Source URL</dt>
                <dd className="text-slate-900 break-all mb-1 sm:mb-0">
                  <a href={row.source_url} target="_blank" rel="noopener noreferrer" className="text-brand-600 hover:underline">
                    {row.source_url} <ExternalLink className="inline h-3 w-3" aria-hidden="true" />
                  </a>
                </dd>
              </>
            )}
            {row.site_domain && (
              <>
                <dt className="text-slate-500">Site</dt>
                <dd className="text-slate-900 mb-1 sm:mb-0">{row.site_domain}</dd>
              </>
            )}
            <dt className="text-slate-500">Pages</dt>
            <dd className="text-slate-900 tabular-nums mb-1 sm:mb-0">
              {row.page_count ?? "—"}
              {row.page_range && <span className="text-slate-500"> (pages {row.page_range} of {row.source_page_count})</span>}
            </dd>
            <dt className="text-slate-500">Forms</dt>
            <dd className="text-slate-900 tabular-nums mb-1 sm:mb-0">{row.forms_detected}</dd>
            {row.sha256 && (
              <>
                <dt className="text-slate-500">SHA-256</dt>
                <dd className="font-mono text-xs text-slate-700 break-all mb-1 sm:mb-0">{row.sha256}</dd>
              </>
            )}
            {row.document_created_at && (
              <>
                <dt className="text-slate-500">Uploaded</dt>
                <dd className="text-slate-700">{new Date(row.document_created_at).toLocaleString()}</dd>
              </>
            )}
          </dl>
        </section>

        {/* Cost summary — super-admin only. */}
        {showCost && (
          <section>
            <h3 className="text-xs uppercase tracking-wider text-slate-500 mb-2">Cost</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="bg-slate-50 rounded px-4 py-3">
                <div className="text-xs text-slate-500">Input tokens</div>
                <div className="text-lg font-semibold text-slate-900 tabular-nums">{formatNum(row.tokens_input)}</div>
              </div>
              <div className="bg-slate-50 rounded px-4 py-3">
                <div className="text-xs text-slate-500">Output tokens</div>
                <div className="text-lg font-semibold text-slate-900 tabular-nums">{formatNum(row.tokens_output)}</div>
              </div>
              <div className="bg-slate-50 rounded px-4 py-3">
                <div className="text-xs text-slate-500">Estimated cost</div>
                <div className="text-lg font-semibold text-slate-900 tabular-nums">{formatCost(row.estimated_cost_usd)}</div>
              </div>
            </div>
          </section>
        )}

        {/* Stage log */}
        <section>
          <h3 className="text-xs uppercase tracking-wider text-slate-500 mb-2">
            Stage log {stages.length > 0 && <span className="text-slate-400">({stages.length})</span>}
          </h3>
          {stages.length === 0 ? (
            <div className="text-sm text-slate-500 italic">No stage log recorded yet.</div>
          ) : (
            <ul className="space-y-2">
              {stages.map((s, i) => {
                const isBad = s.status === "degraded" || s.status === "failed";
                const widthPct = Math.max(2, Math.round((s.duration_ms / maxDuration) * 100));
                return (
                  <li
                    key={`${s.name}-${i}`}
                    className={`rounded border px-3 py-2 ${isBad ? "bg-rose-50 border-rose-200" : "bg-white border-slate-200"}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="font-mono text-sm text-slate-900 truncate">{s.name}</span>
                        <span className={`pill pill-${pillForStage(s.status)}`}>{s.status}</span>
                      </div>
                      <div className="text-xs text-slate-500 tabular-nums shrink-0">{s.duration_ms} ms</div>
                    </div>
                    {/* Duration bar */}
                    <div className="mt-2 h-1 bg-slate-100 rounded overflow-hidden">
                      <div
                        className={isBad ? "h-full bg-rose-400" : "h-full bg-slate-400"}
                        style={{ width: `${widthPct}%` }}
                      />
                    </div>
                    {s.model_version && (
                      <div className="mt-2 text-xs text-slate-500 break-all">
                        model: <code className="text-slate-700">{s.model_version}</code>
                      </div>
                    )}
                    {s.notes && (
                      <pre className="mt-2 text-xs text-slate-700 whitespace-pre-wrap break-words font-mono">
                        {s.notes}
                      </pre>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        {row.error && (
          <section>
            <h3 className="text-xs uppercase tracking-wider text-rose-700 mb-2">Error</h3>
            <pre className="text-xs text-rose-800 bg-rose-50 border border-rose-200 rounded px-3 py-2 whitespace-pre-wrap break-words">
              {row.error}
            </pre>
          </section>
        )}
      </div>
    </Modal>
  );
}

function pillForStage(status: StageLogEntry["status"]): string {
  // Reuse the same color scheme as the row status pills.
  switch (status) {
    case "ok": return "succeeded";
    case "degraded": return "degraded";
    case "failed": return "failed";
    case "skipped": return "unverified";
    default: return "unverified";
  }
}
